/**
 * Rapier2D-backed simulation.
 *
 * Wraps `@dimforge/rapier2d-compat` (asm.js / base64-inlined WASM build —
 * no Vite WASM plugin needed; trades a small bundle-size cost for zero
 * bundler config). Exposes the same `ISimulation` interface as the fake.
 *
 * Coordinate convention: world units are pixels with y-up. We pass them
 * directly to Rapier; the renderer flips y at draw time.
 */

import RAPIER from '@dimforge/rapier2d-compat';
import { BEDROOM_DEFAULT, bedTopY, getArchetype } from '@meowntain/content';
import type { CatId, CatState, ImpactEvent } from '@meowntain/core';
import { applyImpact, decayImpact, updateCatPressure } from '@meowntain/core';
import type { ISimulation, WorldPoint } from './simulation.js';

/** Gravity pointing down. Rapier wants y-down internally too. */
const GRAVITY: RAPIER.Vector = { x: 0, y: 800 };

interface CatRecord {
  catId: CatId;
  archetypeId: string;
  bodyHandle: number;
  /** Cached state object — mutated in place each step. */
  state: CatState;
}

/**
 * Real Rapier2D-backed implementation.
 *
 * Lifecycle:
 *   1. await sim.init() — loads WASM, builds the world, builds the static
 *      sleeper from BEDROOM_DEFAULT segments.
 *   2. sim.spawnCat(...) — adds dynamic cats above the bed.
 *   3. sim.step(dt) — runs physics, recomputes loadAbove + pressureRatio,
 *      surfaces impact events.
 *   4. sim.getCatStates() / sim.drainImpactEvents() — pulled by core/render
 *      each frame.
 */
export class RapierSimulation implements ISimulation {
  private world: RAPIER.World | null = null;
  private eventQueue: RAPIER.EventQueue | null = null;
  private cats: CatRecord[] = [];
  private nextId = 0;
  private events: ImpactEvent[] = [];
  /** Body handle → CatId lookup for collision-event resolution. */
  private bodyHandleToCatId = new Map<number, CatId>();

  async init(): Promise<void> {
    await RAPIER.init();
    this.world = new RAPIER.World(GRAVITY);
    this.eventQueue = new RAPIER.EventQueue(true);
    this.buildSleeperBed();
  }

  /** Build the static "sleeper" surface as a polyline of segments. */
  private buildSleeperBed(): void {
    const world = this.requireWorld();
    const segments = BEDROOM_DEFAULT.segments;
    if (segments.length < 2) return;

    // Rapier expects y-down; our world is y-up. We invert at the boundary
    // by negating y, then the renderer doesn't need any further flip.
    // Actually we keep y-up everywhere and just configure gravity to +y so
    // "down" in render space is "down" here too. Pixel y positive = down.
    // (Renderer mirrors only the heightfield drawing, not coordinates.)

    // Build a polyline collider from the segments. The bed sits at the
    // bottom of the playfield in screen-coords. We treat segment.y as the
    // *inset from the top of the bed*, with the bed surface at the bottom.
    const surfaceY = bedTopY(BEDROOM_DEFAULT.playfieldHeight);

    const points: number[] = [];
    for (const seg of segments) {
      // Higher seg.y in content space = firmer/taller; we want it visually
      // *higher up* on screen, i.e. *smaller* render-y. Sleepers don't
      // dramatically change shape, so a 6-10px range is plenty.
      const renderY = surfaceY - seg.y * 0.4;
      points.push(seg.x);
      points.push(renderY);
    }

    const bodyDesc = RAPIER.RigidBodyDesc.fixed();
    const body = world.createRigidBody(bodyDesc);
    const colliderDesc = RAPIER.ColliderDesc.polyline(new Float32Array(points))
      .setFriction(0.95)
      .setRestitution(0);
    world.createCollider(colliderDesc, body);

    // Walls so cats don't fall off the sides — cosmetic for milestone 1.
    const leftWall = world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
    world.createCollider(
      RAPIER.ColliderDesc.cuboid(4, BEDROOM_DEFAULT.playfieldHeight)
        .setTranslation(-4, BEDROOM_DEFAULT.playfieldHeight / 2)
        .setFriction(0.5),
      leftWall,
    );
    const rightWall = world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
    world.createCollider(
      RAPIER.ColliderDesc.cuboid(4, BEDROOM_DEFAULT.playfieldHeight)
        .setTranslation(BEDROOM_DEFAULT.bedWidth + 4, BEDROOM_DEFAULT.playfieldHeight / 2)
        .setFriction(0.5),
      rightWall,
    );
  }

  step(dtSeconds: number): void {
    const world = this.requireWorld();
    const eventQueue = this.eventQueue;
    if (!eventQueue) return;

    world.timestep = dtSeconds;
    world.step(eventQueue);

    // Update cat states from physics.
    for (const cat of this.cats) {
      const body = world.getRigidBody(cat.bodyHandle);
      if (!body) continue;
      const t = body.translation();
      const v = body.linvel();
      cat.state.x = t.x;
      cat.state.y = t.y;
      cat.state.rotation = body.rotation();
      cat.state.vx = v.x;
      cat.state.vy = v.y;
      decayImpact(cat.state, dtSeconds);
    }

    // Surface contact-event impacts for the impact system.
    eventQueue.drainContactForceEvents((event) => {
      const a = event.collider1();
      const b = event.collider2();
      const colliderA = world.getCollider(a);
      const colliderB = world.getCollider(b);
      if (!colliderA || !colliderB) return;
      const handleA = colliderA.parent()?.handle;
      const handleB = colliderB.parent()?.handle;
      if (handleA === undefined || handleB === undefined) return;
      const catA = this.bodyHandleToCatId.get(handleA) ?? null;
      const catB = this.bodyHandleToCatId.get(handleB) ?? null;
      // We only care about cat-on-cat or cat-on-sleeper impacts.
      if (catA === null && catB === null) return;
      const impulse = event.totalForceMagnitude() * dtSeconds;
      // Decide which is upper (higher render-y means lower in screen — wait,
      // y is screen-down here, so a *smaller* y means physically higher).
      // For impact purposes, the body with the *larger negative dy* (faster
      // falling) is the impacting "upper" body.
      let upper: CatId | null;
      let lower: CatId | 'sleeper';
      if (catA === null) {
        upper = catB;
        lower = 'sleeper';
      } else if (catB === null) {
        upper = catA;
        lower = 'sleeper';
      } else {
        const stateA = this.cats.find((c) => c.catId === catA)?.state;
        const stateB = this.cats.find((c) => c.catId === catB)?.state;
        if (!stateA || !stateB) return;
        if (stateA.y < stateB.y) {
          upper = catA;
          lower = catB;
        } else {
          upper = catB;
          lower = catA;
        }
      }
      if (!upper) return;
      const evt: ImpactEvent = { upperCatId: upper, lowerCatId: lower, impulse };
      this.events.push(evt);
      // Apply to the receiving cat right away.
      const lowerState =
        lower === 'sleeper' ? null : this.cats.find((c) => c.catId === lower)?.state;
      if (lowerState) applyImpact(lowerState, evt);
    });

    // Recompute pressureRatio from the contact graph. We walk every cat
    // in y-ascending screen order (= top of stack first in physical space),
    // sum the masses of cats touching from above, and propagate. Capped
    // at depth 8 implicitly by Rapier returning at most that many contacts
    // per body in our tower geometry.
    this.recomputePressure(dtSeconds);
  }

  /**
   * Per-cat raw load is the sum of (mass + loadAbove) of every cat
   * touching from above. Smoothing is applied by core's updateCatPressure.
   */
  private recomputePressure(dtSeconds: number): void {
    const world = this.requireWorld();
    // Process cats top-to-bottom in screen space (y small → large) so
    // upper cats have their loadAbove resolved first.
    const ordered = [...this.cats].sort((a, b) => a.state.y - b.state.y);
    const rawLoad = new Map<CatId, number>();
    for (const cat of ordered) rawLoad.set(cat.catId, 0);

    // Walk contact pairs and attribute upper-cat mass/load to lower cats.
    for (const cat of ordered) {
      const body = world.getRigidBody(cat.bodyHandle);
      if (!body) continue;
      const archetype = getArchetype(cat.archetypeId);
      const contributedAbove = rawLoad.get(cat.catId) ?? 0;
      const contributionToBelow = archetype.massKg + contributedAbove;
      // For each collider attached to this body, walk contact pairs.
      const colliderCount = body.numColliders();
      for (let i = 0; i < colliderCount; i++) {
        const collider = body.collider(i);
        world.contactPairsWith(collider, (otherCollider) => {
          const otherBody = otherCollider.parent();
          if (!otherBody) return;
          const otherCatId = this.bodyHandleToCatId.get(otherBody.handle);
          if (!otherCatId) return; // sleeper or wall
          const otherState = this.cats.find((c) => c.catId === otherCatId)?.state;
          if (!otherState) return;
          // Only contribute downward (other is below this cat).
          if (otherState.y > cat.state.y) {
            const prev = rawLoad.get(otherCatId) ?? 0;
            rawLoad.set(otherCatId, prev + contributionToBelow);
          }
        });
      }
    }

    // Apply smoothed pressure update.
    for (const cat of this.cats) {
      const archetype = getArchetype(cat.archetypeId);
      const raw = rawLoad.get(cat.catId) ?? 0;
      updateCatPressure(cat.state, archetype, raw, dtSeconds);
    }
  }

  spawnCat(archetypeId: string, position: WorldPoint): CatId {
    const world = this.requireWorld();
    const archetype = getArchetype(archetypeId);
    const catId: CatId = `cat_${this.nextId++}`;
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(position.x, position.y)
      .setLinearDamping(0.4)
      .setAngularDamping(0.6)
      .setCcdEnabled(true);
    const body = world.createRigidBody(bodyDesc);
    const halfW = archetype.width / 2;
    const halfH = archetype.height / 2;
    const colliderDesc = RAPIER.ColliderDesc.cuboid(halfW, halfH)
      .setDensity(archetype.massKg / (archetype.width * archetype.height) || 1)
      .setFriction(archetype.friction)
      .setRestitution(archetype.restitution)
      .setActiveEvents(RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS)
      .setContactForceEventThreshold(50);
    world.createCollider(colliderDesc, body);

    const state: CatState = {
      catId,
      archetypeId,
      x: position.x,
      y: position.y,
      rotation: 0,
      vx: 0,
      vy: 0,
      loadAboveKg: 0,
      pressureRatio: 0,
      lastImpactImpulse: 0,
    };
    const record: CatRecord = { catId, archetypeId, bodyHandle: body.handle, state };
    this.cats.push(record);
    this.bodyHandleToCatId.set(body.handle, catId);
    return catId;
  }

  getCatStates(): readonly CatState[] {
    return this.cats.map((c) => c.state);
  }

  removeCat(catId: CatId): void {
    const world = this.world;
    if (!world) return;
    const idx = this.cats.findIndex((c) => c.catId === catId);
    if (idx < 0) return;
    const [cat] = this.cats.splice(idx, 1);
    if (!cat) return;
    const body = world.getRigidBody(cat.bodyHandle);
    if (body) {
      world.removeRigidBody(body);
      this.bodyHandleToCatId.delete(cat.bodyHandle);
    }
  }

  drainImpactEvents(): readonly ImpactEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  destroy(): void {
    this.world?.free();
    this.eventQueue?.free();
    this.world = null;
    this.eventQueue = null;
    this.cats = [];
    this.bodyHandleToCatId.clear();
    this.events = [];
  }

  private requireWorld(): RAPIER.World {
    if (!this.world) throw new Error('RapierSimulation.init() not called');
    return this.world;
  }
}
